import * as angular from 'angular';
import * as _ from 'lodash';

import { GroupChangeBase } from '../groupChangeBase';
import { guidEmpty } from '../../../common';
import { State, FlatGroup, Identifier, GroupState, ComponentDefinition } from '../../../models';
import { IStateService } from '../../../services';
import { ValueTypes } from '../../../common/ValueTypes';


class GroupChangesRules extends GroupChangeBase implements angular.IComponentController {
    display: any[];
    isLoading: boolean;
    states: { [guid: string]: State } = {};

    private readonly _missing: string;

    static $inject = ['stateService', '$translate'];
    constructor(
        private _stateService: IStateService,
        private _$translate: angular.translate.ITranslateService
    ) {
        super();
        this._missing = this._$translate.instant('missing rule');
    }

    $onInit() {
        if (this.master) {
            const self = this;
            this.share.diff.applyAllFromTemplate = () => this.applyAllFromTemplate(self);
            this.share.diff.applyAllFromGroup = () => this.applyAllFromGroup(self);
            this.share.diff.ignoreAll = () => this.ignoreAll(self);
        }
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        if (changesObj['groups'] || changesObj['templates'] || changesObj['changes']) {
            this.refresh();
        }
    }

    private refresh(): void {
        this.isLoading = true;

        this._stateService.getAll()
            .then(data => {
                data.forEach(d => this.states[d.state.guid] = d.state);
                this.display = [];

                if (this.groups && this.templates && this.changes) {
                    this.groups.forEach(group => {
                        const template = _.find(this.templates, t => group.templateGuid === t.guid);
                        if (template) {
                            this.display.push(this.buildViewGroup(group, template));
                        }
                    });
                    this.createAlignButtons();
                }

            }).finally(() => {
                this.isLoading = false;
            });
    }

    private createAlignButtons() {
        const self = this;
        this.share.central = [];

        this.display.forEach(d => {
            this.share.central.push({});

            d.identifiers.forEach(i => {
                this.share.central.push({});

                i.states.forEach(s => {

                    let action = {};
                    if (s.rules.length > 0) {
                        action = {
                            leftAction: this.hasAction(s, r => r.missGroup)
                                ? () => this.action(self, s, self.share.diff.applyFromGroup)
                                : undefined,
                            leftSelected: () => self.share.selectedDiff.fromGroup[s.changeGuid],
                            ignoreAction: () => this.action(self, s, self.share.diff.ignore),
                            ignoreSelected: () => self.share.selectedDiff.ignores[s.changeGuid],
                            rightAction: this.hasAction(s, r => r.missTemplate)
                                ? () => this.action(self, s, self.share.diff.applyFromTemplate)
                                : undefined,
                            rightSelected: () => self.share.selectedDiff.fromTemplate[s.changeGuid]
                        };


                    }
                    this.share.central.push(action);

                    s.rules.forEach(r => this.share.central.push({}));
                });
            });
        });
    }

    private hasAction(state, sel: (r) => boolean) {
        return !_.some(state.rules, r => sel(r));
    }

    private action(self, state, actionMethod: (c) => void) {
        _.flatten(state.rules
            .filter(r => r.change)
            .map(r => r.change.differenceCollection))
            .forEach(c => {
                actionMethod(c);
            });
    }

    private buildViewGroup(group: FlatGroup, template: FlatGroup): any {
        return {
            guid: group.guid,
            groupName: group.name,
            templateName: template.name,
            depth: (<any>group).depth,
            isDifferent: false,
            changeGuid: guidEmpty,
            change: undefined,
            identifiers: this.buildViewIdentifiers(group, template)
        };
    }

    private buildViewIdentifiers(group: FlatGroup, template: FlatGroup): any[] {
        const result: any[] = [];

        group.identifierCollection.forEach(idt => {
            const tpl = _.find(template.identifierCollection, t => t.guid === idt.templateGuid);
            if (tpl && (this.hasRules(idt, group) || this.hasRules(tpl, template))) {
                const vm = {
                    identifierType: idt.type,
                    groupName: idt.name,
                    templateName: tpl.name,
                    states: this.buildViewStates(group, template, idt, tpl)
                };

                result.push(vm);
            }
        });

        return result;
    }

    private buildViewStates(group: FlatGroup, template: FlatGroup,
        groupIdentifier: Identifier, templateIdentifier: Identifier): any[] {
        const result: any[] = [];

        group.groupStateCollection.forEach(gs => {
            const hasDiff = this.hasRulesWithDifference(groupIdentifier, gs);
            const state = this.states[gs.stateGuid];
            if (hasDiff) {
                const vm = {
                    name: (<any>state).name,
                    rules: this.buildViewRules(template, gs, groupIdentifier, templateIdentifier),
                    changeGuid: groupIdentifier.guid + gs.stateGuid
                };

                result.push(vm);
            }
        });

        template.groupStateCollection.forEach(ts => {
            const hasDiff = this.hasRulesWithDifference(templateIdentifier, ts);
            if (hasDiff) {
                const state = this.states[ts.stateGuid];
                const vm = {
                    name: (<any>state).name,
                    rules: this.buildViewRules(template, ts, groupIdentifier, templateIdentifier),
                    changeGuid: templateIdentifier.guid + ts.stateGuid
                };
                result.push(vm);
            }
        });

        return result;
    }

    private buildViewRules(
        template: FlatGroup,
        groupState: GroupState,
        groupIdentifier: Identifier,
        templateIdentifier: Identifier
    ): any[] {
        let result: any[] = [];
        const templateState = _.find(template.groupStateCollection, gs => gs.stateGuid === groupState.stateGuid);

        if (templateState) {
            result = result.concat(this.getGroupViews(groupState, groupIdentifier, templateState, templateIdentifier));
            // Find missing RC from template
            result = result.concat(this.getTemplateViews(templateState, templateIdentifier, groupState, groupIdentifier));
            result = result.filter(d => !!d);
        }

        return result;
    }

    private getTemplateViews(templateState: GroupState, templateIdentifier: Identifier, groupState: GroupState, groupIdentifier: Identifier) {
        return templateState.rangeConditionCollection
            .filter(rc => rc.identifierGuid === templateIdentifier.guid)
            .map(rc => {
                const changes = this.getChanges(rc);
                if (changes) {
                    const groupRc = _.find(groupState.rangeConditionCollection,
                        grc => grc.identifierGuid === groupIdentifier.guid && grc.valueType === rc.valueType);
                    return {
                        change: changes,
                        groupName: this.getLabel(groupRc),
                        missGroup: groupRc ? false : true,
                        missTemplate: false,
                        templateName: this.getLabel(rc)
                    };
                }
            });
    }

    private getGroupViews(groupState: GroupState, groupIdentifier: Identifier, templateState: GroupState, templateIdentifier: Identifier) {
        return groupState.rangeConditionCollection
            .filter(rc => rc.identifierGuid === groupIdentifier.guid)
            .map(rc => {
                const tplRc = _.find(templateState.rangeConditionCollection, t => t.identifierGuid === templateIdentifier.guid && t.valueType === rc.valueType);
                const changes = this.getChanges(rc);
                return {
                    change: changes,
                    groupName: this.getLabel(rc),
                    missGroup: false,
                    missTemplate: tplRc ? false : true,
                    templateName: this.getLabel(tplRc),
                };
            });
    }

    private getLabel(rangeCondition) {

        if (rangeCondition) {
            const valueType: any = ValueTypes.ruleValueType[rangeCondition.valueType];
            const translate = this._$translate.instant(valueType.value);
            return `${translate} [${rangeCondition.min} - ${rangeCondition.max}]`;
        }

        return this._missing;
    }

    private hasRules(identifier: Identifier, group: FlatGroup) {
        const rules = _.flatten(_.map(group.groupStateCollection, gs => gs.rangeConditionCollection));
        return _.some(rules, r => r.identifierGuid === identifier.guid);
    }

    private hasRulesWithDifference(identifier: Identifier, groupState: GroupState) {
        return _.some(this.changes, c => _.some(groupState.rangeConditionCollection, rc =>
            c.objectGuid === rc.guid && rc.identifierGuid === identifier.guid));
    }

    private getChanges(groupRc: any): any {
        const change = _.find(this.changes, c =>
            (c.objectGuid === groupRc.guid));

        return change;
    }

    isTreatedRule(rule): boolean {
        return rule.change
            ? _.some(rule.change.differenceCollection.map(c => c.guid), g => this.isTreated(g))
            : false;
    }

    isSelectedRule(rule): boolean {
        const selected: { [guid: string]: any } = (this.master)
            ? this.share.selectedDiff.fromTemplate
            : this.share.selectedDiff.fromGroup;

        return rule.change
            ? _.some(rule.change.differenceCollection.map(c => c.guid), (g: string) => selected[g] !== undefined)
            : false;
    }

    isTreatedState(state): boolean {
        return _.every(state.rules, r => this.isTreatedRule(r));
    }

    isSelectedState(state): boolean {
        return _.every(state.rules, r => this.isSelectedRule(r));
    }

    applyAllFromTemplate(self: this) {
        this.display.forEach(d => d.identifiers.forEach(i => i.states.forEach(s => {
            if (!self.isTreatedState(s)) {
                this.action(self, s, this.hasAction(s, r => r.missTemplate)
                    ? this.share.diff.applyFromTemplate
                    : this.share.diff.ignore);
            }
        })));
    }

    applyAllFromGroup(self: this) {
        this.display.forEach(d => d.identifiers.forEach(i => i.states.forEach(s => {
            if (!self.isTreatedState(s)) {
                this.action(self, s, this.hasAction(s, r => r.missGroup)
                    ? this.share.diff.applyFromGroup
                    : this.share.diff.ignore);
            }
        })));
    }

    ignoreAll(self: this) {
        this.display.forEach(d => d.identifiers.forEach(i => i.states.forEach(s => {
            if (!self.isTreatedState(s)) {
                this.action(self, s, this.share.diff.ignore);
            }
        })));
    }
}

export const wtGroupChangesRulesWidget: ComponentDefinition = {
    componentId: 'wtGroupChangesRules',
    controller: GroupChangesRules,
    controllerAs: 'vm',
    template: require('./groupChangesRules.component.html'),
    bindings: {
        changes: '<',
        groups: '<',
        templates: '<',
        master: '=?',
        share: '='
    }
};
